import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Stack,
  Pagination,
  PaginationItem,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Drawer,
  Divider,
  useTheme,
  useMediaQuery,
  ImageList,
  ImageListItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LabelIcon from '@mui/icons-material/Label';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
// T-038 移植：這三個 import 原本落在檔案中段，agent 端的 eslint 放行、
// booking 端把 import/first 當 error → build 失敗。搬到 import 區。
import MessageBubble from './chat/MessageBubble';
import ImageViewer from './ImageViewer';
import faqService from '../services/faqService';

// Plan 068 Day 4 — uniform neutral chip styling. Same shape as the chip in
// FaqEditDialog.js audio panel. Text varies, color does not.
const AUDIO_NEUTRAL_CHIP_SX = {
  bgcolor: '#f5f5f5',
  color: 'rgba(0,0,0,0.72)',
  border: '1px solid rgba(0,0,0,0.12)',
  fontWeight: 500,
  fontSize: '11px',
  height: '22px'
};

const AUDIO_STATUS_LABELS = {
  ready: '已就緒',
  stale: '需更新',
  pending: '排隊中',
  generating: '產生中',
  failed: '失敗',
  missing: '未產生'
};

// audio_file_path is slug-less by convention (Asterisk prepends <slug>/ at
// runtime). Without slug we can't construct S3/CloudFront preview URL — see
// FaqEditDialog.js for full reasoning. Return null so the 「已部署於電話系統」
// hint shows uniformly, matching pre-068 manual paths.
const buildFaqAudioUrl = (_audioFilePath) => null;


const FaqContent = ({
  loading,
  error,
  faqs,
  paginatedFaqs,
  renderTextWithClickableUrls,
  renderKeywords,
  getCategoryColor,
  handleEdit,
  handleDelete,
  page,
  rowsPerPage,
  totalPages,
  handlePageChange,
  handleRowsPerPageChange,
  sortOrder,
  searchQuery,
  processingFaqIds = new Set(),
  faqTagAnswersMap = {},
  voiceEnabled = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State for preview drawer
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFaq, setPreviewFaq] = useState(null);
  const [previewImages, setPreviewImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);

  // Plan 068 Day 4 — single shared <audio> element so only one row plays at a
  // time. Tracks which faqId is currently playing.
  const [playingFaqId, setPlayingFaqId] = useState(null);
  const audioElRef = useRef(null);

  const handleToggleAudio = (faq) => {
    const audioPath = faq.audioFilePath || faq.audio_file_path;
    if (!audioPath) return;
    if (playingFaqId === faq.id) {
      // Stop
      if (audioElRef.current) audioElRef.current.pause();
      setPlayingFaqId(null);
      return;
    }
    // Stop any current playback first
    if (audioElRef.current) audioElRef.current.pause();
    const url = buildFaqAudioUrl(audioPath);
    if (!url) return;
    if (!audioElRef.current) {
      audioElRef.current = new Audio();
      audioElRef.current.addEventListener('ended', () => setPlayingFaqId(null));
      audioElRef.current.addEventListener('error', () => setPlayingFaqId(null));
    }
    audioElRef.current.src = url;
    audioElRef.current.play().then(() => setPlayingFaqId(faq.id)).catch(() => setPlayingFaqId(null));
  };

  useEffect(() => () => {
    if (audioElRef.current) {
      audioElRef.current.pause();
      audioElRef.current = null;
    }
  }, []);
  
  // States for ImageViewer
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageViewerIndex, setImageViewerIndex] = useState(0);

  // Load images when preview FAQ changes
  useEffect(() => {
    if (previewFaq && previewOpen) {
      loadPreviewImages(previewFaq);
    } else {
      setPreviewImages([]);
    }
  }, [previewFaq, previewOpen]);

  // Load images for preview
  const loadPreviewImages = async (faqData) => {
    try {
      setImagesLoading(true);
      
      let imageIds = [];
      let existingImages = [];
      
      if (faqData.images && Array.isArray(faqData.images)) {
        existingImages = faqData.images;
        imageIds = faqData.images.map(img => img.id).filter(id => id);
      } else if (faqData.imageIds && Array.isArray(faqData.imageIds)) {
        imageIds = faqData.imageIds;
        existingImages = imageIds.map(id => ({ id }));
      }
      
      if (imageIds.length === 0) {
        setPreviewImages([]);
        setImagesLoading(false);
        return;
      }
      
      setPreviewImages(existingImages.map(img => ({
        id: img.id,
        url: `/api/images/proxy/${img.id}`,
        name: img.name || img.filename || `image-${img.id}`,
        size: img.size || img.fileSize || 0,
        loading: true
      })));
      
      try {
        const imageUrls = await faqService.getBatchImageProxyUrls(imageIds, false);
        
        setPreviewImages(imageUrls.map((urlData, index) => ({
          id: urlData.id,
          url: urlData.url,
          name: existingImages[index]?.name || existingImages[index]?.filename || `image-${urlData.id}`,
          size: existingImages[index]?.size || existingImages[index]?.fileSize || 0,
          loading: false
        })));
      } catch (error) {
        setPreviewImages(prev => prev.map(img => ({ ...img, loading: false })));
      }
      
      setImagesLoading(false);
    } catch (error) {
      setPreviewImages([]);
      setImagesLoading(false);
    }
  };

  const handlePreview = (faq) => {
    setPreviewFaq(faq);
    setPreviewOpen(true);
  };

  const handlePreviewImageClick = (index) => {
    if (!previewImages[index].loading) {
      setImageViewerIndex(index);
      setImageViewerOpen(true);
    }
  };

  // 建立模擬對話訊息
  const createChatMessages = (faq) => {
    if (!faq) return [];

    const now = new Date();
    const userMessageTime = new Date(now.getTime() - 30000);
    const agentMessageTime = new Date(now.getTime() - 10000);

    return [
      {
        id: `user-${faq.id}`,
        content: faq.question,
        contentType: 'text',
        senderType: 'agent',
        timestamp: userMessageTime.toISOString(),
        isRead: true,
        metadata: {}
      },
      {
        id: `agent-${faq.id}`,
        content: faq.answer,
        contentType: 'text',
        senderType: 'user',
        timestamp: agentMessageTime.toISOString(),
        isRead: true,
        metadata: {}
      }
    ];
  };

  return (
    <>
      {/* 可捲動的表格區域 */}
      <Box 
        className="scrollable-area"
        sx={{ 
          flex: 1,
          overflow: 'auto',
          px: 3,
          '&::-webkit-scrollbar': {
            width: '6px'
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '3px'
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(0,0,0,0.3)'
          }
        }}
      >
        {/* Loading */}
        {loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>
              載入中...
            </Typography>
          </Box>
        )}
        
        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table */}
        {!loading && !error && faqs.length > 0 && (
          <TableContainer 
            component={Paper} 
            sx={{ 
              mt: 2,
              position: 'relative',
              maxHeight: 'calc(100vh - 250px)',
              overflow: 'auto'
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell 
                    width="5%" 
                    align="center" 
                    sx={{ bgcolor: '#ddd', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2 }}
                  >
                    編號
                  </TableCell>
                  <TableCell 
                    width="10%" 
                    sx={{ bgcolor: '#ddd', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2 }}
                  >
                    分類
                  </TableCell>
                  <TableCell 
                    width="25%" 
                    sx={{ bgcolor: '#ddd', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2 }}
                  >
                    問題
                  </TableCell>
                  <TableCell 
                    width="35%" 
                    sx={{ bgcolor: '#ddd', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2 }}
                  >
                    回答
                  </TableCell>
                  <TableCell
                    width={voiceEnabled ? "12%" : "15%"}
                    sx={{ bgcolor: '#ddd', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2 }}
                  >
                    關鍵詞
                  </TableCell>
                  {voiceEnabled && (
                    <TableCell
                      width="10%"
                      align="center"
                      sx={{ bgcolor: '#ddd', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2 }}
                    >
                      語音
                    </TableCell>
                  )}
                  <TableCell
                    width="10%"
                    align="center"
                    sx={{ bgcolor: '#ddd', fontWeight: 'bold', position: 'sticky', top: 0, zIndex: 2 }}
                  >
                    操作
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedFaqs.map((faq, index) => (
                  <TableRow key={faq.id || `row-${faq.displayNumber}`}>
                    <TableCell align="center">
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {sortOrder === 'asc' 
                          ? (page - 1) * rowsPerPage + index + 1 
                          : faqs.length - ((page - 1) * rowsPerPage + index)
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {faq.category ? (
                        <Chip 
                          label={faq.category} 
                          color={getCategoryColor(faq.category)}
                          size="small"
                          sx={{ fontSize: '0.75rem', height: 24 }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {renderTextWithClickableUrls(faq.question)}

                        {/* 🔥 NEW: 處理中標記 — AI 嵌入向量尚未就緒 */}
                        {processingFaqIds.has(faq.id) && (
                          <Tooltip title="AI 分析處理中，完成後即可搜尋此 FAQ">
                            <Chip
                              icon={
                                <AutorenewIcon
                                  sx={{
                                    fontSize: 14,
                                    animation: 'spin 1.5s linear infinite',
                                    '@keyframes spin': {
                                      '0%': { transform: 'rotate(0deg)' },
                                      '100%': { transform: 'rotate(360deg)' }
                                    }
                                  }}
                                />
                              }
                              label="處理中"
                              size="small"
                              variant="outlined"
                              color="warning"
                              sx={{
                                fontSize: '0.65rem',
                                height: 22,
                                flexShrink: 0,
                                '& .MuiChip-icon': {
                                  marginLeft: '4px'
                                }
                              }}
                            />
                          </Tooltip>
                        )}

                        {/* 圖片圖示 */}
                        {(faq.imageIds?.length > 0 || faq.images?.length > 0) && (
                          <Tooltip title={`${faq.imageIds?.length || faq.images?.length} 張圖片`}>
                            <ImageIcon 
                              sx={{ 
                                fontSize: 16, 
                                color: 'action.active',
                                flexShrink: 0
                              }} 
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {renderTextWithClickableUrls(faq.answer)}
                      {/* 標籤回答標記 */}
                      {faqTagAnswersMap[faq.id]?.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                          {faqTagAnswersMap[faq.id].map(ta => (
                            <Chip
                              key={ta.tagId || ta.tag_id}
                              icon={<LabelIcon sx={{ fontSize: 12 }} />}
                              label={`${ta.tag?.name || ta.Tag?.name || ta.tagName} 專屬回答`}
                              size="small"
                              sx={{
                                height: 20, fontSize: '11px',
                                bgcolor: `${ta.tag?.color || ta.Tag?.color || ta.tagColor || '#607D8B'}20`,
                                color: ta.tag?.color || ta.Tag?.color || ta.tagColor || '#607D8B',
                                border: '1px solid',
                                borderColor: `${ta.tag?.color || ta.Tag?.color || ta.tagColor || '#607D8B'}60`,
                                '& .MuiChip-icon': { color: ta.tag?.color || ta.Tag?.color || ta.tagColor || '#607D8B' }
                              }}
                            />
                          ))}
                          {!faq.answer && (
                            <Chip
                              label="僅限標籤用戶"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '11px' }}
                            />
                          )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {renderKeywords(faq.raw_keywords || faq.raw_keyword)}
                    </TableCell>
                    {voiceEnabled && (() => {
                      const status = faq.audioStatus ?? faq.audio_status ?? 'missing';
                      const audioPath = faq.audioFilePath || faq.audio_file_path;
                      // Pre-068 manual paths (e.g. "qa_audio/<name>") have a
                      // GSM on Asterisk for callers but no S3 WAV mirror, so
                      // there's nothing the browser can play. Restrict canPlay
                      // to paths buildFaqAudioUrl can resolve.
                      const canPlay = !!audioPath
                        && (status === 'ready' || status === 'stale')
                        && !!buildFaqAudioUrl(audioPath);
                      const isPlaying = playingFaqId === faq.id;
                      const inFlight = status === 'pending' || status === 'generating';
                      const label = AUDIO_STATUS_LABELS[status] || '未產生';
                      return (
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                              size="small"
                              label={
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                  {inFlight && (
                                    <CircularProgress size={9} thickness={6} sx={{ color: 'rgba(0,0,0,0.55)' }} />
                                  )}
                                  <span>{label}</span>
                                </Box>
                              }
                              sx={AUDIO_NEUTRAL_CHIP_SX}
                              title={faq.audioDuration ? `時長 ${faq.audioDuration} 秒` : undefined}
                            />
                            {canPlay && (
                              <Tooltip title={isPlaying ? '停止播放' : '播放語音'}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleAudio(faq)}
                                  sx={{ p: 0.25 }}
                                >
                                  {isPlaying
                                    ? <StopIcon fontSize="small" />
                                    : <PlayArrowIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })()}
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="預覽對話">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handlePreview(faq)}
                            sx={{ 
                              bgcolor: 'rgba(25, 118, 210, 0.05)',
                              '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.15)' }
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="編輯">
                          <IconButton
                            color="info"
                            size="small"
                            onClick={() => handleEdit(faq.id)}
                            sx={{ 
                              bgcolor: 'rgba(3, 169, 244, 0.05)',
                              '&:hover': { bgcolor: 'rgba(3, 169, 244, 0.15)' }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="刪除">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDelete(faq.id)}
                            sx={{ 
                              bgcolor: 'rgba(244, 67, 54, 0.05)',
                              '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.15)' }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {/* 無結果 */}
        {!loading && !error && faqs.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {searchQuery ? '沒有符合搜尋條件的結果' : '沒有常見問題資料'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* 底部分頁 */}
      {!loading && !error && faqs.length > 0 && (
        <Box sx={{ 
          flexShrink: 0,
          backgroundColor: '#f5f5f5',
          borderTop: '1px solid #e0e0e0',
          p: 2,
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center'
        }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            shape="rounded"
            showFirstButton={false}
            showLastButton={false}
            siblingCount={1}
            boundaryCount={1}
            renderItem={(item) => {
              if (item.type === 'previous') {
                return (
                  <PaginationItem
                    key={`prev-${page}`}
                    {...item}
                    sx={{ border: '1px solid #e0e0e0', borderRadius: '4px' }}
                    icon={<ChevronLeftIcon />}
                  />
                );
              }
              if (item.type === 'next') {
                return (
                  <PaginationItem
                    key={`next-${page}`}
                    {...item}
                    sx={{ border: '1px solid #e0e0e0', borderRadius: '4px' }}
                    icon={<ChevronRightIcon />}
                  />
                );
              }
              return (
                <PaginationItem
                  key={`page-${item.page || 'ellipsis'}`}
                  {...item}
                  sx={{ 
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    '&.Mui-selected': {
                      backgroundColor: '#e3f2fd',
                      color: '#000',
                      borderColor: '#90caf9'
                    }
                  }}
                />
              );
            }}
          />
          
          <Box component="span" sx={{ mx: 2 }}>...</Box>
          
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              displayEmpty
              sx={{ 
                height: 36,
                '& .MuiSelect-select': { display: 'flex', alignItems: 'center', pl: 2 }
              }}
              MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
            >
              <MenuItem key="rows-10" value={10}>10</MenuItem>
              <MenuItem key="rows-20" value={20}>20</MenuItem>
              <MenuItem key="rows-50" value={50}>50</MenuItem>
              <MenuItem key="rows-100" value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {/* 對話預覽 Drawer */}
      <Drawer
        anchor="right"
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: '420px' },
            borderRadius: { xs: 0, sm: '12px 0 0 12px' },
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            height: 'calc(100% - 48px)',
            margin: { xs: 0, sm: '24px 0' },
          },
          transition: 'transform 0.3s ease-in-out',
        }}
      >
        {/* Header */}
        <Box sx={{ 
          bgcolor: 'primary.main',
          color: 'white',
          p: 2, 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(255,255,255,0.12)'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            對話預覽
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => setPreviewOpen(false)}
            sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Chat Content */}
        {previewFaq && (
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              bgcolor: '#f8f7f5',
              display: 'flex',
              flexDirection: 'column',
              p: 2
            }}
          >
            {createChatMessages(previewFaq).map((message, index) => (
              <MessageBubble
                key={message.id}
                message={message}
                isConsecutive={false}
              />
            ))}
            
            {/* 預覽圖片 */}
            {previewImages.length > 0 && (
              <Box sx={{ 
                mt: 2, 
                mb: 2,
                mr: 'auto',
                maxWidth: '95%',
                bgcolor: 'white',
                borderRadius: 2,
                p: 2,
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
                  附件圖片 ({previewImages.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {previewImages.map((image, index) => (
                    <Box
                      key={image.id}
                      sx={{
                        position: 'relative',
                        width: '100%',
                        paddingTop: '100%',
                        cursor: image.loading ? 'default' : 'pointer',
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid #e0e0e0',
                        bgcolor: '#f5f5f5',
                        '&:hover': {
                          '& img': { transform: image.loading ? 'none' : 'scale(1.05)' }
                        }
                      }}
                      onClick={() => handlePreviewImageClick(index)}
                    >
                      {image.loading ? (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <CircularProgress size={30} />
                        </Box>
                      ) : (
                        <img
                          src={image.url}
                          alt={image.name}
                          loading="lazy"
                          style={{
                            position: 'absolute',
                            top: 0, left: 0,
                            width: '100%', height: '100%',
                            objectFit: 'contain',
                            transition: 'transform 0.2s',
                            backgroundColor: '#f5f5f5'
                          }}
                          onError={(e) => {
                            if (!image.fallbackAttempted) {
                              e.target.src = `/api/images/proxy/${image.id}`;
                              setPreviewImages(prev => prev.map(img => 
                                img.id === image.id 
                                  ? { ...img, fallbackAttempted: true }
                                  : img
                              ));
                            }
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
                {imagesLoading && (
                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      載入圖片中...
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* 輸入欄（停用） */}
        <Box 
          sx={{
            p: 2,
            borderTop: '1px solid rgba(0,0,0,0.12)',
            bgcolor: '#f5f5f5'
          }}
        >
          <Paper
            component="div"
            sx={{
              p: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              borderRadius: '24px',
              bgcolor: '#ffffff',
              border: '1px solid #e0e0e0'
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ color: 'text.secondary', flex: 1, fontStyle: 'italic' }}
            >
              輸入訊息...
            </Typography>
          </Paper>
        </Box>
      </Drawer>

      {/* ImageViewer */}
      <ImageViewer
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        images={previewImages.map(img => ({
          ...img,
          fallbackUrl: `/api/images/proxy/${img.id}`
        }))}
        initialIndex={imageViewerIndex}
      />
    </>
  );
};

export default FaqContent;